import { Routes } from '@angular/router';
import { ROUTE_SEGMENTS } from './core/constants/app-routes';
import { adminGuard } from './core/guards/admin.guard';
import { clientsGuard } from './core/guards/clients.guard';
import { fieldReviewGuard } from './core/guards/field-review.guard';
import { userManagementGuard } from './core/guards/user-management.guard';
import { authGuard } from './core/guards/auth.guard';
import { clientOwnerGuard } from './core/guards/client-owner.guard';
import { reservationsEnabledGuard } from './core/guards/reservations-enabled.guard';
import { consumerGuard } from './core/guards/consumer.guard';
import { dirtyFormGuard } from './core/guards/dirty-form.guard';
import { routeAdminGuard } from './core/guards/route-admin.guard';

const S = ROUTE_SEGMENTS;

/** Rotas públicas de cadastro (QR, aceite, autovalidação). */
const publicCadastroRoutes: Routes = [
  {
    path: S.guide,
    loadComponent: () =>
      import('./pages/public/guide-index/guide-index.component').then((m) => m.GuideIndexComponent)
  },
  {
    path: `${S.guide}/:cidade`,
    loadComponent: () =>
      import('./pages/public/guide-city/guide-city.component').then((m) => m.GuideCityComponent)
  },
  {
    path: `${S.intake}/:cidade/${S.privacy}`,
    loadComponent: () =>
      import('./pages/public/intake-privacy/intake-privacy.component').then(
        (m) => m.IntakePrivacyComponent
      )
  },
  {
    path: `${S.intake}/:cidade`,
    loadComponent: () =>
      import('./pages/public/city-intake-wizard/city-intake-wizard.component').then(
        (m) => m.CityIntakeWizardComponent
      )
  },
  {
    path: S.intake,
    loadComponent: () =>
      import('./pages/intake/intake-page.component').then((m) => m.IntakePageComponent)
  },
  {
    path: `${S.selfFill}/:token`,
    loadComponent: () =>
      import('./pages/self-fill/self-fill.component').then((m) => m.SelfFillComponent)
  },
  {
    path: `${S.landing}/:id`,
    loadComponent: () =>
      import('./pages/landing/landing-page.component').then((m) => m.LandingPageComponent)
  },
  {
    path: `${S.landingPersonalization}/:id`,
    loadComponent: () =>
      import('./pages/landing-personalization/landing-personalization-page.component').then(
        (m) => m.LandingPersonalizationPageComponent
      )
  },
  {
    path: S.digitalYearbook,
    loadComponent: () =>
      import('./pages/public/digital-yearbook/digital-yearbook-page.component').then(
        (m) => m.DigitalYearbookPageComponent
      )
  },
  {
    path: S.passaporte,
    loadComponent: () =>
      import('./pages/public/passaporte/passaporte-page.component').then(
        (m) => m.PassaportePageComponent
      )
  },
  {
    path: `${S.passaporte}/entrar`,
    loadComponent: () =>
      import('./pages/public/passaporte/passaporte-auth.component').then(
        (m) => m.PassaporteAuthComponent
      ),
    data: { mode: 'entrar' }
  },
  {
    path: `${S.passaporte}/cadastro`,
    loadComponent: () =>
      import('./pages/public/passaporte/passaporte-auth.component').then(
        (m) => m.PassaporteAuthComponent
      ),
    data: { mode: 'cadastro' }
  },
  {
    path: `${S.passaporte}/conta`,
    canActivate: [consumerGuard],
    loadComponent: () =>
      import('./pages/public/passaporte/passaporte-club-page.component').then(
        (m) => m.PassaporteClubPageComponent
      )
  },
  {
    path: `${S.passaporte}/:id/reservar/carro/:itemId`,
    loadComponent: () =>
      import('./pages/public/passaporte/reserve/car-book.component').then((m) => m.CarBookComponent)
  },
  {
    path: `${S.passaporte}/:id/reservar/hospedagem/:itemId`,
    loadComponent: () =>
      import('./pages/public/passaporte/reserve/stay-book.component').then((m) => m.StayBookComponent)
  },
  {
    path: `${S.passaporte}/:id/reservar/restaurante`,
    loadComponent: () =>
      import('./pages/public/passaporte/reserve/table-book.component').then((m) => m.TableBookComponent)
  },
  {
    path: `${S.passaporte}/:id`,
    loadComponent: () =>
      import('./pages/public/passaporte/passaporte-company-page.component').then(
        (m) => m.PassaporteCompanyPageComponent
      )
  },
  {
    path: `${S.clientHomologation}/:id`,
    loadComponent: () =>
      import('./pages/homologation-confirm/homologation-confirm.component').then(
        (m) => m.HomologationConfirmComponent
      )
  },
  {
    path: `${S.conference}/:token`,
    loadComponent: () =>
      import('./pages/public/conference-page/conference-page.component').then(
        (m) => m.ConferencePageComponent
      )
  },
  {
    path: `${S.contract}/:token`,
    loadComponent: () =>
      import('./pages/public/contract-page/contract-page.component').then(
        (m) => m.ContractPageComponent
      )
  },
  {
    path: `${S.acceptance}/:token`,
    loadComponent: () =>
      import('./pages/acceptance/acceptance-page.component').then(
        (m) => m.AcceptancePageComponent
      )
  },
  {
    path: `${S.empresa}/${S.selfValidation}/:token`,
    loadComponent: () =>
      import('./pages/self-validation/self-validation-page.component').then(
        (m) => m.SelfValidationPageComponent
      )
  },
  {
    path: `${S.empresa}/:cidade/:slug`,
    loadComponent: () =>
      import('./pages/public/passaporte/passaporte-company-page.component').then(
        (m) => m.PassaporteCompanyPageComponent
      )
  }
];

/** Cadastro autenticado: empresas, validação e trilha de auditoria. */
const companyCadastroRoutes: Routes = [
  {
    path: S.companies,
    loadComponent: () =>
      import('./pages/companies/companies.component').then((m) => m.CompaniesComponent)
  },
  {
    path: `${S.companies}/:id/${S.validate}`,
    loadComponent: () =>
      import('./pages/company-validation/company-validation.component').then(
        (m) => m.CompanyValidationComponent
      )
  },
  {
    path: `${S.companies}/:id/${S.commercialFormalization}`,
    loadComponent: () =>
      import('./pages/audit/commercial-formalization/commercial-formalization.component').then(
        (m) => m.CommercialFormalizationComponent
      )
  },
  {
    path: `${S.companies}/:id/${S.audit}/${S.call}`,
    loadComponent: () =>
      import('./pages/audit/call-verification/call-verification.component').then(
        (m) => m.CallVerificationComponent
      )
  },
  {
    path: `${S.companies}/:id/${S.audit}/${S.visit}`,
    loadComponent: () =>
      import('./pages/audit/on-site-visit/on-site-visit.component').then(
        (m) => m.OnSiteVisitComponent
      )
  },
  {
    path: `${S.companies}/:id/${S.audit}/${S.physical}`,
    loadComponent: () =>
      import('./pages/audit/physical-audit/physical-audit.component').then(
        (m) => m.PhysicalAuditComponent
      )
  },
  {
    path: `${S.companies}/:id/${S.audit}/${S.homologation}`,
    loadComponent: () =>
      import('./pages/audit/homologation/homologation.component').then(
        (m) => m.HomologationComponent
      )
  }
];

/** Cadastro de usuários (admin). `:id` estático `novo` vem antes do parâmetro dinâmico. */
const userCadastroRoutes: Routes = [
  {
    path: S.users,
    canActivate: [userManagementGuard],
    loadComponent: () =>
      import('./pages/users/users.component').then((m) => m.UsersComponent)
  },
  {
    path: `${S.users}/${S.usersNew}`,
    canActivate: [userManagementGuard],
    loadComponent: () =>
      import('./pages/user-form/user-form.component').then((m) => m.UserFormComponent)
  },
  {
    path: `${S.users}/:id/${S.usersEdit}`,
    canActivate: [userManagementGuard],
    loadComponent: () =>
      import('./pages/user-form/user-form.component').then((m) => m.UserFormComponent)
  },
  {
    path: `${S.users}/:id`,
    canActivate: [userManagementGuard],
    loadComponent: () =>
      import('./pages/user-detail/user-detail.component').then((m) => m.UserDetailComponent)
  }
];

export const routes: Routes = [
  {
    path: 'privacidade-passaporte',
    loadComponent: () =>
      import('./pages/public/privacy-notice/privacy-notice-page.component').then(
        (m) => m.PrivacyNoticePageComponent
      )
  },
  {
    path: S.home,
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/home/home.component').then((m) => m.HomeComponent)
  },
  {
    path: S.login,
    loadComponent: () =>
      import('./pages/login/login.component').then((m) => m.LoginComponent)
  },
  ...publicCadastroRoutes,
  {
    path: S.underConstruction,
    loadComponent: () =>
      import('./pages/under-construction/under-construction.component').then(
        (m) => m.UnderConstructionComponent
      )
  },
  {
    path: S.underMaintenance,
    loadComponent: () =>
      import('./pages/under-maintenance/under-maintenance.component').then(
        (m) => m.UnderMaintenanceComponent
      )
  },
  {
    path: S.portal,
    redirectTo: `${S.field}/${S.fieldDashboard}`,
    pathMatch: 'full'
  },
  {
    path: '',
    loadComponent: () =>
      import('./layout/portal-shell.component').then((m) => m.PortalShellComponent),
    canActivate: [authGuard],
    children: [
      {
        path: S.myCompany,
        canActivate: [clientOwnerGuard],
        loadComponent: () =>
          import('./pages/client-owner/client-owner-page.component').then(
            (m) => m.ClientOwnerPageComponent
          )
      },
      {
        path: `${S.myCompany}/${S.referrals}`,
        canActivate: [clientOwnerGuard],
        loadComponent: () =>
          import('./pages/client-owner-referrals/client-owner-referrals.component').then(
            (m) => m.ClientOwnerReferralsComponent
          )
      },
      {
        path: `${S.myCompany}/${S.menu}`,
        canActivate: [clientOwnerGuard],
        loadComponent: () =>
          import('./pages/client-owner/menu/client-owner-menu.component').then(
            (m) => m.ClientOwnerMenuComponent
          )
      },
      {
        path: `${S.myCompany}/${S.reservations}`,
        canActivate: [clientOwnerGuard, reservationsEnabledGuard],
        loadComponent: () =>
          import('./pages/client-owner/reservations/reservations-home.component').then(
            (m) => m.ReservationsHomeComponent
          )
      },
      ...companyCadastroRoutes,
      {
        path: S.visitAgenda,
        loadComponent: () =>
          import('./pages/visit-agenda/visit-agenda.component').then((m) => m.VisitAgendaComponent)
      },
      {
        path: `${S.field}/${S.fieldDashboard}`,
        loadComponent: () =>
          import('./pages/field/dashboard/field-dashboard.component').then(
            (m) => m.FieldDashboardComponent
          )
      },
      {
        path: `${S.field}/${S.myRoute}`,
        loadComponent: () =>
          import('./pages/field/my-route/my-route.component').then((m) => m.MyRouteComponent)
      },
      {
        path: `${S.field}/${S.fieldSheet}/:enrichmentId`,
        canDeactivate: [dirtyFormGuard],
        loadComponent: () =>
          import('./pages/field/field-sheet/field-sheet.component').then(
            (m) => m.FieldSheetComponent
          )
      },
      {
        path: `${S.field}/${S.clientBriefing}/:enrichmentId`,
        loadComponent: () =>
          import('./pages/field/client-briefing/client-briefing.component').then(
            (m) => m.ClientBriefingComponent
          )
      },
      {
        path: `${S.field}/${S.fieldRoutes}`,
        canActivate: [routeAdminGuard],
        loadComponent: () =>
          import('./pages/field/route-admin/route-admin.component').then(
            (m) => m.RouteAdminComponent
          )
      },
      {
        path: `${S.field}/${S.fieldAudit}`,
        redirectTo: `/${S.clients}`,
        pathMatch: 'full'
      },
      {
        path: `${S.field}/${S.fieldHomologation}`,
        redirectTo: `/${S.clients}`,
        pathMatch: 'full'
      },
      {
        path: `${S.field}/${S.fieldFinalSheet}/:enrichmentId`,
        loadComponent: () =>
          import('./pages/field/field-final-sheet/field-final-sheet.component').then(
            (m) => m.FieldFinalSheetComponent
          )
      },
      {
        path: `${S.field}/${S.fieldReviews}`,
        canActivate: [fieldReviewGuard],
        loadComponent: () =>
          import('./pages/field/field-reviews/field-reviews.component').then(
            (m) => m.FieldReviewsComponent
          )
      },
      {
        path: `${S.field}/${S.fieldDiscoveries}`,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/field/field-discoveries/field-discoveries.component').then(
            (m) => m.FieldDiscoveriesComponent
          )
      },
      {
        path: `${S.field}/${S.fieldSelfValidation}`,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/field/field-self-validation/field-self-validation.component').then(
            (m) => m.FieldSelfValidationComponent
          )
      },
      {
        path: `${S.field}/${S.fieldTeams}`,
        loadComponent: () =>
          import('./pages/field/team-console/team-console.component').then(
            (m) => m.TeamConsoleComponent
          )
      },
      {
        path: `${S.field}/${S.fieldSupportQueue}`,
        loadComponent: () =>
          import('./pages/field/support-queue/support-queue.component').then(
            (m) => m.SupportQueueComponent
          )
      },
      {
        path: `${S.field}/${S.vipEditorial}`,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/field/vip-editorial/vip-editorial.component').then(
            (m) => m.VipEditorialComponent
          )
      },
      {
        path: `${S.clients}/:id/personalizar-vitrine`,
        canActivate: [clientsGuard],
        loadComponent: () =>
          import('./pages/clients/client-landing-personalization.component').then(
            (m) => m.ClientLandingPersonalizationComponent
          )
      },
      {
        path: S.clients,
        canActivate: [clientsGuard],
        loadComponent: () =>
          import('./pages/clients/clients.component').then((m) => m.ClientsComponent)
      },
      {
        path: S.yearbook,
        loadComponent: () =>
          import('./pages/yearbook/yearbook-page.component').then((m) => m.YearbookPageComponent)
      },
      {
        path: S.faq,
        loadComponent: () => import('./pages/faq/faq-page.component').then((m) => m.FaqPageComponent)
      },
      {
        path: S.tutorials,
        loadComponent: () =>
          import('./pages/tutoriais/tutoriais-page.component').then((m) => m.TutoriaisPageComponent)
      },
      {
        path: `${S.admin}/${S.adminDataOps}`,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/data-ops/data-ops.component').then((m) => m.DataOpsComponent)
      },
      {
        path: `${S.admin}/${S.platformSettings}`,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/platform-settings/platform-settings.component').then(
            (m) => m.PlatformSettingsComponent
          )
      },
      {
        path: `${S.admin}/${S.adminLodging}`,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/lodging-types/admin-lodging-types.component').then(
            (m) => m.AdminLodgingTypesComponent
          )
      },
      {
        path: `${S.admin}/${S.adminFleet}`,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/fleet/admin-fleet.component').then((m) => m.AdminFleetComponent)
      },
      {
        path: S.cities,
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/cities/city-hub/city-hub.component').then((m) => m.CityHubComponent)
      },
      ...userCadastroRoutes
    ]
  },
  { path: '**', redirectTo: S.home }
];
